import type { RefObject } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Flex, Tooltip, Typography, theme } from 'antd';

export interface TextTooltipEchoProps {
  popoverRef: RefObject<HTMLDivElement>;
  title?: string;
  description: string;
  buttonText?: string;
  onMouseOver?: () => void;
}

export const TextTooltipContentEcho = ({
  title,
  description,
}: Pick<TextTooltipEchoProps, 'title' | 'description'>) => {
  const { token } = theme.useToken();
  const inverseTextStyle = { color: token.colorTextLightSolid };
  return (
    <Flex vertical data-testid="text-tooltip-echo-content" style={{ maxWidth: 320 }}>
      {title ? (
        <Typography.Text strong style={{ ...inverseTextStyle, marginBottom: token.marginXXS }}>
          {title}
        </Typography.Text>
      ) : null}
      <Typography.Paragraph style={{ ...inverseTextStyle, fontSize: token.fontSizeSM, margin: 0 }}>
        {description}
      </Typography.Paragraph>
    </Flex>
  );
};

/**
 * Ant Design–based tooltip trigger with optional label text and an info icon.
 * Mirrors {@link TextTooltip} for Echo surfaces.
 */
const TextTooltipEcho = ({
  popoverRef,
  title,
  description,
  buttonText,
  onMouseOver,
}: TextTooltipEchoProps) => {
  const { token } = theme.useToken();

  const tooltipTitle = <TextTooltipContentEcho title={title} description={description} />;

  return (
    <Flex
      ref={popoverRef}
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseOver={onMouseOver}
    >
      <Tooltip placement="topLeft" title={tooltipTitle}>
        <Flex
          align="center"
          gap={buttonText ? token.marginXS : 6}
          style={{ cursor: 'default', color: token.colorTextSecondary }}
          aria-label={title ? `${title} tooltip` : 'Tooltip'}
        >
          {buttonText}
          <InfoCircleOutlined style={{ fontSize: token.fontSizeSM, flexShrink: 0 }} aria-hidden />
        </Flex>
      </Tooltip>
    </Flex>
  );
};

export default TextTooltipEcho;
